import { useEffect, useState } from 'react';
import { Accordion, Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { To, useNavigate, useParams } from 'react-router-dom';
import { SFCAccordion } from '../../../../../components/accordion/SFCAccordion';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { SimpleTable } from '../../../../../components/tables/SimpleTable';
import { useToast } from '../../../../../context/ToastContext';
import {
  useApproveReceiptsMutation,
  useDeleteReceiptsMutation,
  useDenyReceiptsMutation,
  useGetReceiptQuery
} from '../../../../../services/endpoints/finance/receipt';
import InputModal from '../../../../../components/modals/InputModal';
import { Receipt } from '../../../../../types/finance.types';
import { revisionsColumns } from '../../../../../utils/consts/columnDefs/sessionColumns';
import { dateToUTC } from '../../../../../utils/dateUtils';
import { getErrorMessage, getPartition } from '../../../../../utils/utils';
import { getCurrentUser } from '../../../../../services/helper';
import { canSeeFinancials } from '../../../../../utils/consts';
import { ReceiptImage } from './ReceiptImage';
import { downloadReceiptImage } from '../../../../../services/api';

export const ReceiptDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data, error, isLoading, refetch } = useGetReceiptQuery(+params.id!);
  const [receipt, setReceipt] = useState<Receipt>();
  const [deleteReceipt, { isLoading: isDeleting }] = useDeleteReceiptsMutation();
  const [approveReceipt, { isLoading: isApproving }] = useApproveReceiptsMutation();
  const [denyReceipt, { isLoading: isDenying }] = useDenyReceiptsMutation();
  const [showModal, setShowModal] = useState(false);
  const [showDenyModal, setShowDenyModal] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading receipt', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setReceipt(data.receipt);
    }
  }, [data]);

  const handleDelete = async () => {
    try {
      await deleteReceipt({ ids: [+params.id!] }).unwrap();
      addToast('Receipt deleted successfully', 'success');
      navigate('/admin/finance/receipts');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleApprove = async () => {
    try {
      await approveReceipt({ ids: [+params.id!] }).unwrap();
      addToast('Receipt approved', 'success');
      refetch();
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleDeny = async (reason: string) => {
    try {
      await denyReceipt({ ids: [+params.id!], reason_for_denial: reason }).unwrap();
      addToast('Receipt denied', 'success');
      setShowDenyModal(false);
      refetch();
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleDownloadImage = async () => {
    if (!receipt?.id || !receipt.image_file_name) return;
    try {
      await downloadReceiptImage(receipt.id, receipt.image_file_name);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (isLoading || !receipt) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  let image_url = receipt.signed_url ?? `${receipt.s3_base_url ?? ''}/${getPartition(receipt.id)}/original/${receipt.image_file_name}`;

  const RECEIPT_STATUS_LABELS: Record<number, string> = {
    0: 'Pending',
    1: 'Denied',
    2: 'Approved'
  };
  const statusNum = Number(receipt?.status);
  const isPending = statusNum === 0;

  const currentUser = getCurrentUser();
  const userTypeId = currentUser?.user_type_id;
  const canManageReceipt = userTypeId !== undefined && canSeeFinancials(userTypeId);

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate(-1 as To)}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Receipts List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`Receipt #${receipt?.id}`}</h2>
        </Stack>
        <Stack
          direction="horizontal"
          gap={1}
          className="d-none d-lg-flex"
        >
          {canManageReceipt && isPending && (
              <>
                <SimpleButton
                    variant="primary"
                    height={36}
                    onClick={handleApprove}
                    disabled={isApproving}
                >
                  Approve
                </SimpleButton>
                <SimpleButton
                    variant="primary"
                    height={36}
                    onClick={() => setShowDenyModal(true)}
                    disabled={isDenying}
                >
                  Deny
                </SimpleButton>
              </>
          )}
          <SimpleButton
            variant="primary"
            height={36}
            onClick={() => navigate(`/admin/finance/receipts/${receipt?.id}/edit`)}
          >
            Edit receipt
          </SimpleButton>
          {canManageReceipt && (
              <SimpleButton
                  variant="primary"
                  height={36}
                  onClick={() => setShowModal(true)}
              >
                Delete Receipt
              </SimpleButton>
          )}
        </Stack>
      </div>
      <div style={{ width: 'calc(100% - 40px)', marginLeft: '20px', marginTop: '20px' }}>
        <SFCAccordion
          className="sfc-accordion--mobile-body-visible"
          defaultActiveKey={canManageReceipt ? ['0', '1'] : ['0']}
        >
          <Accordion.Item eventKey="0">
            <Accordion.Header>Receipt Details</Accordion.Header>
            <Accordion.Body>
              <Stack
                gap={4}
                className="p-4"
              >
                <DetailSection>
                  <LabelText label="Instructor:">
                    <LinkCell
                      content={receipt?.user?.full_name}
                      url={`/admin/dash-users/instructors/${receipt?.user?.id}`}
                      underline
                    />
                  </LabelText>
                  <LabelText label="Session:">
                    <LinkCell
                      content={receipt?.course?.title ?? 'No session title'}
                      url={`/admin/schedule/sessions/${receipt?.course?.id}`}
                      underline
                    />
                  </LabelText>
                  <LabelText
                    label="Store Name:"
                    text={receipt?.store_name}
                  />
                  <LabelText
                    label="Date of Purchase:"
                    text={
                      receipt?.purchased_at
                        ? dateToUTC(new Date(receipt?.purchased_at), 'MMMM dd, yyyy')
                        : 'N/A'
                    }
                  />
                  <LabelText
                    label="Total:"
                    text={`$${receipt?.total.toFixed(2)}`}
                  />
                  {/* TODO: add image icon/thumbnail */}
                  <LabelText
                      label="Status:"
                      text={RECEIPT_STATUS_LABELS[statusNum] ?? 'N/A'}
                  />
                  {receipt?.reason_for_denial && (
                      <LabelText
                          label="Reason for Denial:"
                          text={receipt.reason_for_denial}
                      />
                  )}
                  <LabelText
                      label="Image:"
                      text={receipt?.image_file_name ?? 'N/A'}
                  />
                  <div style={{ maxWidth: 500, width: '100%' }}>
                    <ReceiptImage
                        url={image_url}
                        fileName={receipt?.image_file_name}
                        alt={`Receipt #${receipt?.id}`}
                        receiptId={receipt.id}
                    />
                  </div>
                  <LabelText label="Download:">
                    <button
                      type="button"
                      className="btn btn-link p-0 text-info fw-bolder"
                      style={{ fontSize: '.875rem', textDecoration: 'underline' }}
                      onClick={handleDownloadImage}
                      disabled={!receipt?.image_file_name}
                    >
                      {receipt?.image_file_name ?? 'N/A'}
                    </button>
                  </LabelText>
                </DetailSection>
              </Stack>
            </Accordion.Body>
          </Accordion.Item>
          {canManageReceipt && (
            <Accordion.Item eventKey="1">
              <Accordion.Header>Revisions</Accordion.Header>
              <Accordion.Body>
                <div className="d-none d-lg-block">
                  <SimpleTable
                    columns={revisionsColumns}
                    data={
                      receipt?.audits?.map((audit) => ({
                        ...audit,
                        receipt_id: receipt.id
                      })) ?? []
                    }
                  />
                </div>
              </Accordion.Body>
            </Accordion.Item>
          )}
        </SFCAccordion>
      </div>
      <ConfirmationModal
        show={showModal}
        title="Delete Receipt"
        message="Are you sure you want to delete this receipt?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isDeleting}
      />
      <InputModal
          show={showDenyModal}
          title="Reason for Denial"
          message=""
          placeholder=""
          onClose={() => setShowDenyModal(false)}
          onSubmit={(value) => handleDeny(value)}
      />
    </>
  );
};
